import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { theme } from 'styles/global-styles';
import { makeStyles } from 'tss-react/mui';
import { createTheme, Grid, StyledEngineProvider, ThemeProvider, TextField } from '@mui/material';
import _ from 'lodash';

type ComplianceStatusProgressSearchBoxProps = {
    fullScreen: boolean;
};

const smallTextFieldTheme = createTheme(theme, {
    components: {
        MuiFormControl: {
            styleOverrides: {
                root: {
                    height: '37.625px',
                    width: '100%',
                    margin: '6px 0px',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: { height: '37.625px' },
            },
        },
    }
})

const useStyles = makeStyles<{ fullScreen: boolean }>()((theme, { fullScreen }) => ({
    searchContainer: {
        backgroundColor: theme.palette.system.lightGray,
        padding: '12px 16px'
    },
    textField: {
        fontSize: 12,
        width: '100%',
        '&::placeholder': {
            opacity: 1,
            fontSize: 12,
            color: '#C0C0C0',
        },
    },
}));

export default function ComplianceStatusProgressSearchBox(complianceStatusProgressSearchBox:
    Readonly<ComplianceStatusProgressSearchBoxProps>) {
    const { fullScreen } = complianceStatusProgressSearchBox;
    const { classes } = useStyles({ fullScreen });
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');
    const dispatch = useDispatch();

    const throttleService = useMemo(
        () => _.throttle((searchText = '') => dispatch(placementDetailsAction.searchComplianceStatusNotes(searchText)), 1000),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const handleSearch = React.useCallback(
        searchText => {
            setSearch(searchText);
            throttleService(searchText);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [throttleService],
    );
    return (
        <Grid container className={classes.searchContainer}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={smallTextFieldTheme}>
                    <TextField
                        onChange={event => {
                            handleSearch(event.target.value);
                        }}
                        value={search ?? ''}
                        autoFocus
                        placeholder={t('placement.profile.complianceStatusProgress.searchPlaceholder')}
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 255 }}
                        InputProps={{
                            classes: { input: classes.textField },
                            startAdornment: <SearchIcon style={{ opacity: 0.6 }} />,
                            'aria-label': 'filter-type-ahead',
                        }}
                    />
                </ThemeProvider>
            </StyledEngineProvider>
        </Grid>
    )
}